import { z } from 'zod';

const optionalId = z.union([z.string(), z.number()]).optional().nullable();
const optionalAmount = z.union([z.string(), z.number(), z.null()]).optional().nullable();

const salesOrderLineFormSchema = z.object({
  id: optionalId,
  customerOrderLineId: optionalId,
  productId: optionalId,
  qty: z.union([z.string(), z.number(), z.null()]),
  remark: z.string().optional().nullable(),
  product: z.any().optional().nullable(),
  unitPrice: optionalAmount,
  /** SO 價格倍率 [0,1]（0.9＝九折）；空＝後端預設 1。勿與 CO 折扣比例混用。 */
  discountRate: optionalAmount,
  totalAmount: optionalAmount,
  mtrs: z.array(z.any()).optional().nullable(),
  invAllocations: z.array(z.any()).optional().nullable(),
  extraItems: z.array(z.any()).optional().nullable(),
}).superRefine((line, ctx) => {
  const qty = Number(line.qty);
  if (line.qty === '' || line.qty == null || Number.isNaN(qty) || qty <= 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: '數量須大於 0',
      path: ['qty'],
    });
  }

  if (line.discountRate !== '' && line.discountRate != null) {
    const rate = Number(line.discountRate);
    if (Number.isNaN(rate) || rate < 0 || rate > 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: '折數須為 0～1（例如 0.9＝九折）',
        path: ['discountRate'],
      });
    }
  }
});

const salesOrderManualLineFormSchema = z.object({
  id: optionalId,
  customerOrderManualLineId: optionalId,
  itemName: z.string().min(1, '請填寫品名'),
  qty: z.union([z.string(), z.number(), z.null()]),
  unitName: z.string().min(1, '請填寫單位'),
  unitPrice: optionalAmount,
  remark: z.string().optional().nullable(),
  grossAmount: optionalAmount,
  netAmount: optionalAmount,
}).superRefine((line, ctx) => {
  const qty = Number(line.qty);
  if (line.qty === '' || line.qty == null || Number.isNaN(qty) || qty <= 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: '數量須大於 0',
      path: ['qty'],
    });
  }
  if (line.unitPrice !== '' && line.unitPrice != null) {
    const price = Number(line.unitPrice);
    if (Number.isNaN(price) || price < 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: '單價不可為負',
        path: ['unitPrice'],
      });
    }
  }
});

export const SALES_ORDER_FORM_DEFAULT_VALUES = {
  customerId: '',
  plannedShipDate: '',
  billingAddressId: '',
  shippingAddressId: '',
  shippingContactId: '',
  currencyId: '',
  taxMode: 'TAX_EXCLUSIVE',
  taxRate: 0.05,
  roundingAdjustment: 0,
  issueReason: '',
  remark: '',
  lines: [],
  manualLines: [],
};

export const salesOrderFormSchema = z.object({
  customerId: optionalId,
  plannedShipDate: z.string().min(1, '請填寫預交日'),
  billingAddressId: z.string().optional().nullable(),
  shippingAddressId: z.string().optional().nullable(),
  shippingContactId: z.string().optional().nullable(),
  currencyId: z.string().min(1, '請選擇幣別'),
  taxMode: z.string().optional().nullable(),
  taxRate: optionalAmount,
  roundingAdjustment: optionalAmount,
  issueReason: z.string().optional().nullable(),
  remark: z.string().optional().nullable(),
  lines: z.array(salesOrderLineFormSchema),
  manualLines: z.array(salesOrderManualLineFormSchema).default([]),
}).superRefine((values, ctx) => {
  const productCount = (values.lines ?? []).length;
  const manualCount = (values.manualLines ?? []).length;
  if (productCount + manualCount < 1) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: '至少一筆產品或其他產品明細',
      path: ['lines'],
    });
  }
});

export const SALES_ORDER_FIELD_LABELS = {
  customerId: '客戶',
  plannedShipDate: '預交日',
  billingAddressId: '帳單地址',
  shippingAddressId: '收貨地址',
  shippingContactId: '收貨聯絡人',
  currencyId: '幣別',
  taxMode: '計稅模式',
  taxRate: '稅率',
  roundingAdjustment: '進位調整',
  issueReason: '事由',
  remark: '備註',
  lines: '明細',
  'lines.discountRate': '折數',
  manualLines: '其他產品',
  'manualLines.itemName': '品名',
  'manualLines.qty': '數量',
  'manualLines.unitName': '單位',
  'manualLines.unitPrice': '單價',
  'manualLines.remark': '行備註',
};

const LINE_FIELD_LABELS = {
  qty: '數量',
  discountRate: '折數',
  remark: '列備註',
};

const MANUAL_LINE_FIELD_LABELS = {
  itemName: '品名',
  qty: '數量',
  unitName: '單位',
  unitPrice: '單價',
  remark: '行備註',
};

export function resolveSalesOrderFieldLabel(path) {
  const lineMatch = path.match(/^lines\.(\d+)\.(.+)$/);
  if (lineMatch) {
    const index = Number(lineMatch[1]) + 1;
    const field = lineMatch[2];
    return `明細 #${index} ${LINE_FIELD_LABELS[field] || field}`;
  }

  const manualMatch = path.match(/^manualLines\.(\d+)\.(.+)$/);
  if (manualMatch) {
    const index = Number(manualMatch[1]) + 1;
    const field = manualMatch[2];
    return `其他產品 #${index} ${MANUAL_LINE_FIELD_LABELS[field] || field}`;
  }

  return SALES_ORDER_FIELD_LABELS[path] ?? path;
}

export const SALES_ORDER_LINES_TIP = (
  '可改數量與列備註。折扣請填折數（例如 0.9＝九折）；空白＝不打折。'
  + '更換產品後請重新設定庫存配置。'
);

export const SALES_ORDER_ADDRESS_TIP = (
  '請選擇帳單地址、出貨地址與收貨聯絡人；未選時系統會依客戶預設帶入。'
);
